import {
  Bot,
  Brain,
  Check,
  Chrome,
  Code2,
  Download,
  ExternalLink,
  Hammer,
  Laptop2,
  MessageSquareText,
  ShieldCheck,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import SlideGuide, {
  type GuideLink,
  type GuideSlide,
  type GuideStep,
  type SlideTone,
} from "@/components/training/SlideGuide";
import { MODULES, type Step } from "@/data/claudeMasterclass";

const MODULE_ICONS: LucideIcon[] = [MessageSquareText, Chrome, Bot, Code2, Hammer];
const MODULE_ACTIVITY_TONES: SlideTone[] = ["paper", "blue", "paper", "blue", "paper"];
const MODULE_DIVIDER_TONES: SlideTone[] = ["dark", "orange", "blue", "orange", "dark"];

const LEARNING_OUTCOMES = [
  "Hold a productive Claude conversation and write prompts that produce usable answers.",
  "Share files and images, then organize ongoing work with Projects, instructions, and memory.",
  "Use Claude for Chrome safely while approving access one site at a time.",
  "Connect Claude to tools like Gmail and Calendar so it can act, not just advise.",
  "Build and improve repeatable workflows with Skills, connections, and a scorecard.",
  "Use Claude Code for practical non-technical work, from websites to document drafting.",
  "Install the meta-prompting toolkit and reuse strong prompts with slash commands.",
  "Use Skill Forge to build, score, package, and share a reusable Claude skill.",
  "Choose the right Claude surface for a task and protect your data deliberately.",
];

const PREREQUISITES = [
  {
    title: "A computer and internet access",
    body: "The lessons work on macOS or Windows. You do not need a coding background.",
    icon: Laptop2,
  },
  {
    title: "A Claude account",
    body: "Free is enough for Chat and limited Projects. Paid access unlocks Chrome, Cowork, and Claude Code.",
    icon: MessageSquareText,
    href: "https://claude.ai",
  },
  {
    title: "Google Chrome",
    body: "Module 2 uses the Claude for Chrome extension and site-by-site permissions.",
    icon: Chrome,
    href: "https://www.google.com/chrome/",
  },
  {
    title: "One real task to improve",
    body: "Follow along hands-on with work you actually repeat. The result will be more useful than a practice-only demo.",
    icon: Sparkles,
  },
];

const KEY_CONCEPTS = [
  ["Prompt", "The message you send Claude. A clear ask, useful context, and an example produce the best result."],
  ["Artifact", "A panel beside the chat where Claude builds a document, table, or small app you can edit in place."],
  ["Browser use", "Claude acting in a browser tab through Claude for Chrome, with access approved site by site."],
  ["Project", "A reusable container for instructions, reference files, and connections for one kind of work."],
  ["Skill", "A plain-text instruction set that gives Claude focused expertise for a specific task."],
  ["Connection (MCP)", "An approved link that lets Claude reach an outside tool such as Gmail or Drive."],
  ["Workspace folder", "The folder opened in Claude Code, which defines the files Claude may work with."],
  ["Safety model", "Claude shows what it plans to do and waits for approval before making important changes."],
  ["Slash command", "A saved instruction run with a shortcut such as /create-prompt."],
  ["Starter pack", "Reusable commands and skills loaded from the .claude folder when Claude Code starts."],
  ["Skill Forge", "A meta-skill that builds, validates, scores, and packages other skills."],
  ["Progressive disclosure", "Loading only the instructions and resources Claude needs at each stage of a task."],
] as const;

const NEXT_STEPS = [
  "Install Claude for Chrome and try one safe browser task on a single approved site.",
  "Build one real Cowork workflow for work you repeat weekly, then refine it until edits are minor.",
  "Create a practice folder and complete the three Claude Code tasks: a one-page site, folder cleanup, and document draft.",
  "Install the meta-prompting starter pack and run /create-prompt on a real task.",
  "Use Skill Forge on one repeated workflow and share the packaged .skill file with a teammate.",
  "Review privacy settings across Claude Chat, Chrome, Cowork, and Code so each reflects a deliberate choice.",
  "Bookmark the official documentation and save your strongest workflow as a reusable Skill.",
];

const TROUBLESHOOTING = [
  ["Claude's answers are vague or off-target.", "Add context and an example, explain what was wrong, and ask for a revision."],
  ["A Cowork connection will not return live data.", "Confirm the connection is signed in and enabled for that Project, then reconnect if needed."],
  ["Claude for Chrome will not act on a website.", "Open the extension and approve that site. Browser access is granted per site."],
  ["A workflow stops partway through.", "Check every required connection and referenced folder. The workflow should report what is missing."],
  ["Claude Code will not open or sign in.", "Quit and reopen it, check the internet connection, then reinstall from the official download page if needed."],
  ["Claude Code changed something you did not want.", "Decline the next action, ask it to undo or revise, and review each proposed change before approval."],
] as const;

const RESOURCE_CARDS = [
  {
    title: "Claude documentation",
    body: "Official product guides and reference material.",
    href: "https://docs.anthropic.com",
    icon: ExternalLink,
    external: true,
  },
  {
    title: "MCP setup guide",
    body: "Official Model Context Protocol documentation.",
    href: "https://docs.anthropic.com/en/docs/mcp",
    icon: ExternalLink,
    external: true,
  },
  {
    title: "Meta-prompting starter pack",
    body: "Install the Claude Code commands and skills used in Module 4.",
    href: "/ai-builder-playbook#step-install-toolkit",
    icon: Download,
  },
  {
    title: "Skill Forge",
    body: "Download the meta-skill from Module 5 and forge a reusable skill.",
    href: "/skill-forge.skill",
    icon: Hammer,
    download: true,
  },
] as const;

interface LessonRecord {
  moduleIndex: number;
  localIndex: number;
  number: number;
  step: Step;
}

const LESSONS: LessonRecord[] = MODULES.flatMap((module, moduleIndex) =>
  module.steps.map((step, localIndex) => ({ moduleIndex, localIndex, step })),
).map((lesson, index) => ({ ...lesson, number: index + 1 }));

const promptsFor = (step: Step) => [
  ...(step.codeBlock
    ? [{ label: step.codeBlock.caption ?? "Copy and run", value: step.codeBlock.code }]
    : []),
  ...(step.examples?.items.map((value, index) => ({
    label: `${step.examples?.caption ?? "Example"}${step.examples && step.examples.items.length > 1 ? ` ${index + 1}` : ""}`,
    value,
  })) ?? []),
];

const linksFor = (step: Step): GuideLink[] => [
  ...(step.download
    ? [{
        label: step.download.label,
        href: step.download.href,
        download: true,
        eventName: step.download.eventName,
        external: false,
      }]
    : []),
  ...(step.links?.map((link) => ({
    label: link.label,
    href: link.href,
    eventName: link.eventName,
    external: /^https?:\/\//.test(link.href),
  })) ?? []),
];

const MASTERCLASS_STEPS: GuideStep[] = LESSONS.map(({ moduleIndex, number, step }) => ({
  id: String(number),
  title: step.title,
  description: step.description,
  duration: `${MODULES[moduleIndex].navLabel} · ${MODULES[moduleIndex].level}`,
  whyItMatters: step.whatThisStepDoes,
  actions: step.actionSteps,
  checkpoint: step.checkpoint,
  icon: MODULE_ICONS[moduleIndex],
  prompts: promptsFor(step),
  images: step.image
    ? [{ src: step.image.src, alt: step.image.alt, caption: step.image.caption ?? step.image.alt }]
    : undefined,
  note: step.download?.note,
  links: linksFor(step),
  reference: step.table
    ? { title: "Quick reference", headers: step.table.headers, rows: step.table.rows }
    : undefined,
}));

const MODULE_RANGES = MODULES.map((module, moduleIndex) => {
  const lessons = LESSONS.filter((lesson) => lesson.moduleIndex === moduleIndex);
  return {
    start: lessons[0].number,
    end: lessons[lessons.length - 1].number,
    count: module.steps.length,
  };
});

const LESSON_SLIDES: GuideSlide[] = MODULES.flatMap((module, moduleIndex) => {
  const lessons = LESSONS.filter((lesson) => lesson.moduleIndex === moduleIndex);
  const tone = MODULE_ACTIVITY_TONES[moduleIndex];

  return [
    {
      id: module.id,
      title: module.navLabel,
      kind: "custom",
      tone: MODULE_DIVIDER_TONES[moduleIndex],
      nextLabel: `Start ${module.navLabel}`,
    },
    ...lessons.flatMap(({ number, step }) => {
      const guideStep = MASTERCLASS_STEPS[number - 1];
      const prompts = guideStep.prompts ?? [];
      const hasPrompts = prompts.length > 0;
      const activity: GuideSlide = {
        id: `step-${number}`,
        title: step.title,
        kind: "activity",
        tone,
        stepId: String(number),
        showWhy: true,
        showImages: Boolean(step.image),
        showReference: Boolean(step.table),
        showLinks: guideStep.links ? guideStep.links.length > 0 : false,
        showNote: Boolean(guideStep.note),
        nextLabel: hasPrompts ? "Open the practice prompt" : "Continue",
      };

      const promptSlides: GuideSlide[] = prompts.map((prompt, promptIndex) => {
        return {
          id: `step-${number}-practice-${promptIndex + 1}`,
          title: prompt.label,
          kind: "prompt",
          tone: promptIndex % 2 === 0 ? "dark" : "blue",
          stepId: String(number),
          promptIndexes: [promptIndex],
          actionIndexes: [Math.max(step.actionSteps.length - 1, 0)],
          nextLabel: promptIndex === prompts.length - 1 ? "Continue" : "Next prompt",
        };
      });

      return [activity, ...promptSlides];
    }),
  ];
});

const MASTERCLASS_SLIDES: GuideSlide[] = [
  { id: "welcome", title: "Claude Masterclass", kind: "cover", tone: "dark", nextLabel: "Start the masterclass" },
  { id: "prerequisites", title: "Before you begin", kind: "custom", tone: "paper", nextLabel: "See what you will learn" },
  { id: "outcomes", title: "What you will learn", kind: "outcomes", tone: "paper", nextLabel: "See the five modules" },
  { id: "five-module-roadmap", title: "Your five-module roadmap", kind: "custom", tone: "dark", nextLabel: "Start with Claude Chat" },
  ...LESSON_SLIDES,
  { id: "key-concepts", title: "Key concepts", kind: "custom", tone: "paper", nextLabel: "Choose your next practice" },
  { id: "next-steps", title: "Put the Masterclass to work", kind: "custom", tone: "blue", nextLabel: "Troubleshoot common issues" },
  { id: "troubleshooting", title: "Troubleshooting", kind: "custom", tone: "paper", nextLabel: "Open the resources" },
  { id: "resources", title: "Additional resources", kind: "custom", tone: "dark", nextLabel: "Finish the Masterclass" },
  { id: "finish", title: "Masterclass complete", kind: "finish", tone: "dark" },
];

const cardClass = (light: boolean) => light
  ? "border-slate-200 bg-white text-slate-950 shadow-sm"
  : "border-white/15 bg-white/[0.06] text-white";

const renderMasterclassSlide = (slide: GuideSlide, { light, printMode }: { light: boolean; printMode: boolean }) => {
  if (slide.id === "prerequisites") {
    return (
      <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-8 sm:py-12">
        <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-orange-700">Before you begin</p>
        <h1 className="mt-4 max-w-5xl font-serif text-4xl font-semibold leading-tight tracking-[-0.04em] sm:text-5xl lg:text-6xl">Four things make the Masterclass easier.</h1>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {PREREQUISITES.map((item) => {
            const Icon = item.icon;
            const content = (
              <div className="flex items-start gap-4">
                <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-orange-300"><Icon className="h-6 w-6" /></span>
                <span><strong className="block text-lg text-slate-950">{item.title}</strong><span className="mt-2 block text-sm leading-relaxed text-slate-600">{item.body}</span></span>
              </div>
            );
            return item.href && !printMode ? <a key={item.title} href={item.href} target="_blank" rel="noopener noreferrer" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-colors hover:border-orange-400">{content}</a> : <div key={item.title} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">{content}</div>;
          })}
        </div>
      </div>
    );
  }

  if (slide.id === "five-module-roadmap") {
    return (
      <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-8 sm:py-12">
        <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-orange-300">The Masterclass</p>
        <h1 className="mt-4 max-w-5xl font-serif text-4xl font-semibold leading-tight tracking-[-0.04em] text-white sm:text-5xl lg:text-6xl">Move from conversation to reusable AI capability.</h1>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {MODULES.map((module, index) => {
            const Icon = MODULE_ICONS[index];
            const range = MODULE_RANGES[index];
            return (
              <a key={module.id} href={`#${module.id}`} className="group rounded-2xl border border-white/15 bg-white/[0.05] p-5 text-white transition-colors hover:border-orange-400 hover:bg-white/[0.09] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-500"><Icon className="h-5 w-5" /></span>
                <span className="mt-5 block text-xs font-extrabold uppercase tracking-[0.15em] text-orange-300">Module {index + 1} · {module.level}</span>
                <strong className="mt-2 block text-lg leading-tight">{module.navLabel}</strong>
                <span className="mt-3 block text-sm leading-relaxed text-slate-400">{module.tagline}</span>
                <span className="mt-5 block text-xs font-bold text-slate-500">Steps {range.start}–{range.end}</span>
              </a>
            );
          })}
        </div>
      </div>
    );
  }

  const moduleIndex = MODULES.findIndex((module) => module.id === slide.id);
  if (moduleIndex >= 0) {
    const module = MODULES[moduleIndex];
    const Icon = MODULE_ICONS[moduleIndex];
    const lessons = LESSONS.filter((lesson) => lesson.moduleIndex === moduleIndex);
    return (
      <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-8 sm:py-12">
        <div className="flex items-center gap-4">
          <span className={`flex h-14 w-14 items-center justify-center rounded-2xl ${light ? "bg-slate-950 text-orange-300" : "bg-white/10 text-orange-200 ring-1 ring-white/15"}`}><Icon className="h-7 w-7" /></span>
          <div><p className={`text-xs font-extrabold uppercase tracking-[0.18em] ${light ? "text-orange-700" : "text-orange-200"}`}>Module {moduleIndex + 1} · {module.level}</p><p className={`mt-1 text-sm font-semibold ${light ? "text-slate-500" : "text-white/70"}`}>{module.tagline}</p></div>
        </div>
        <h1 className="mt-6 max-w-5xl font-serif text-4xl font-semibold leading-tight tracking-[-0.04em] sm:text-5xl lg:text-6xl">{module.navLabel}</h1>
        <p className={`mt-5 max-w-4xl text-base leading-relaxed sm:text-lg ${light ? "text-slate-600" : "text-white/80"}`}>{module.intro}</p>
        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {lessons.map((lesson) => <a key={lesson.number} href={`#step-${lesson.number}`} className={`rounded-2xl border p-4 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 ${cardClass(light)} ${light ? "hover:border-orange-400" : "hover:border-white/35"}`}><span className={`text-xs font-extrabold uppercase tracking-[0.14em] ${light ? "text-orange-700" : "text-orange-200"}`}>Step {lesson.number}</span><strong className="mt-2 block leading-snug">{lesson.step.title}</strong></a>)}
        </div>
      </div>
    );
  }

  if (slide.id === "key-concepts") {
    return (
      <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-8 sm:py-12">
        <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-orange-700">Summary</p>
        <h1 className="mt-4 font-serif text-4xl font-semibold tracking-[-0.04em] sm:text-5xl lg:text-6xl">Keep these twelve ideas straight.</h1>
        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {KEY_CONCEPTS.map(([term, definition]) => <div key={term} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"><div className="flex items-center gap-3"><Brain className="h-5 w-5 text-orange-600" /><strong>{term}</strong></div><p className="mt-3 text-sm leading-relaxed text-slate-600">{definition}</p></div>)}
        </div>
      </div>
    );
  }

  if (slide.id === "next-steps") {
    return (
      <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-8 text-white sm:py-12">
        <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-orange-300">Keep building</p>
        <h1 className="mt-4 max-w-5xl font-serif text-4xl font-semibold leading-tight tracking-[-0.04em] sm:text-5xl lg:text-6xl">Turn one lesson into a habit this week.</h1>
        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          {NEXT_STEPS.map((item, index) => <div key={item} className="flex items-start gap-4 rounded-2xl border border-white/15 bg-white/[0.06] p-5"><span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-orange-500 font-extrabold">{index + 1}</span><p className="pt-1 leading-relaxed text-slate-200">{item}</p></div>)}
        </div>
      </div>
    );
  }

  if (slide.id === "troubleshooting") {
    return (
      <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-8 sm:py-12">
        <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-orange-700">When something gets stuck</p>
        <h1 className="mt-4 font-serif text-4xl font-semibold tracking-[-0.04em] sm:text-5xl lg:text-6xl">Start with the simplest fix.</h1>
        <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          {TROUBLESHOOTING.map(([problem, solution], index) => <div key={problem} className={`grid gap-2 p-4 sm:grid-cols-[0.82fr_1.18fr] sm:gap-6 sm:p-5 ${index ? "border-t border-slate-200" : ""}`}><strong className="text-slate-950">{problem}</strong><p className="text-sm leading-relaxed text-slate-600">{solution}</p></div>)}
        </div>
      </div>
    );
  }

  if (slide.id === "resources") {
    return (
      <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-8 text-white sm:py-12">
        <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-orange-300">Keep nearby</p>
        <h1 className="mt-4 font-serif text-4xl font-semibold tracking-[-0.04em] sm:text-5xl lg:text-6xl">Documentation, commands, and reusable skills.</h1>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {RESOURCE_CARDS.map((resource) => {
            const Icon = resource.icon;
            return <a key={resource.title} href={resource.href} download={resource.download || undefined} target={resource.external ? "_blank" : undefined} rel={resource.external ? "noopener noreferrer" : undefined} className="group rounded-2xl border border-white/15 bg-white/[0.06] p-5 transition-colors hover:border-orange-400 hover:bg-white/[0.1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"><div className="flex items-start gap-4"><span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-orange-500"><Icon className="h-6 w-6" /></span><span><strong className="text-xl">{resource.title}</strong><span className="mt-2 block text-sm leading-relaxed text-slate-400">{resource.body}</span></span></div></a>;
          })}
        </div>
      </div>
    );
  }

  return null;
};

const ClaudeMasterclass = () => (
  <SlideGuide
    name="Claude Masterclass"
    trackingName="Claude Masterclass"
    canonicalPath="/claude-masterclass"
    storageKey="claude-masterclass-progress"
    slideStorageKey="claude-masterclass-current-slide"
    seo={{
      title: "Claude Masterclass: A Step-by-Step Slide Guide",
      description: "Learn Claude Chat, Chrome, Cowork, Code, and Skill Forge in a public five-module, 45-step interactive slide guide for non-technical people.",
      keywords: ["Claude masterclass", "Claude Chat", "Claude for Chrome", "Claude Cowork", "Claude Code", "Claude tutorial", "Claude skills", "Skill Forge", "MCP", "meta prompting"],
    }}
    schema={{
      description: "A five-module Claude Masterclass covering Chat, Chrome, Cowork, Code, and Skill Forge in 45 hands-on lessons.",
      totalTime: "PT6H",
      supplies: ["A computer", "A Claude account", "Google Chrome", "One real workflow to improve"],
    }}
    cover={{
      kicker: "Public, five-module slide masterclass",
      title: "Claude Masterclass",
      accent: "Chat, Chrome, Cowork, Code, and Skill Forge",
      description: "Go from your first Claude conversation to reusable AI workflows and shareable skills. Work one lesson at a time, with every prompt, screenshot, and download kept in context.",
      badges: [`${MODULES.length} modules`, `${MASTERCLASS_STEPS.length} lessons`, "No coding background needed"],
      finishLineTitle: "One useful workflow you can run, improve, and share",
      finishLineBody: "You will know which Claude surface fits the task, how to keep a human in control, and how to package your strongest process as a reusable skill.",
    }}
    outcomes={{
      eyebrow: "What you will be able to do",
      heading: "Move from asking Claude questions to building dependable AI capability.",
      items: LEARNING_OUTCOMES,
      summary: "The course stays practical: every lesson gives you an observable result, and your slide position is saved in this browser.",
    }}
    roadmapHeading="Five modules. Forty-five lessons. One capability at a time."
    steps={MASTERCLASS_STEPS}
    slides={MASTERCLASS_SLIDES}
    contentsExtras={[
      { slideId: "prerequisites", label: "Prerequisites" },
      ...MODULES.map((module, index) => ({ slideId: module.id, label: `Module ${index + 1}: ${module.navLabel}` })),
      { slideId: "key-concepts", label: "Key concepts" },
      { slideId: "troubleshooting", label: "Troubleshooting" },
      { slideId: "resources", label: "Resources" },
    ]}
    finish={{
      eyebrow: "Masterclass complete",
      heading: "You now have a practical path across the Claude ecosystem.",
      body: "Use the AI Tune-Up to choose the next capability to strengthen, or continue into the deeper Code and automation guides.",
      cards: [
        { eyebrow: "Assess", title: "Longhand AI Roadmap", body: "Rate 17 practical capabilities and use the results to choose what to strengthen next.", href: "/ai-30/program" },
        { eyebrow: "Build", title: "The AI Builder Playbook", body: "Turn a Job Brief into a safe, tested prototype and a reviewable handoff package.", href: "/ai-builder-playbook" },
        { eyebrow: "Scale", title: "Build a Reliable Loop", body: "Teach one dependable agent what to repeat, then choose when its workflow should run.", href: "/ai-30/program/atlas#feedback-and-schedule" },
      ],
    }}
    renderCustomSlide={renderMasterclassSlide}
  />
);

export default ClaudeMasterclass;
